import { useState, useEffect, useCallback } from 'react'
import { Search, Trash2, X, Upload } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { PolicyExportButton } from '@/components/policies/policy-export-button'
import type { PolicyDraftStatus } from '@/types/policy'

/**
 * Status filter options
 */
const STATUS_OPTIONS: Array<{ value: PolicyDraftStatus | ''; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
]

interface PoliciesToolbarProps {
  /** Current search query */
  searchQuery: string
  /** Callback when search query changes (debounced) */
  onSearchChange: (query: string) => void
  /** Current status filter */
  statusFilter: PolicyDraftStatus | ''
  /** Callback when status filter changes */
  onStatusFilterChange: (status: PolicyDraftStatus | '') => void
  /** Number of selected items */
  selectedCount: number
  /** Whether any selected items are deployed (can't be bulk deleted) */
  hasDeployedSelected: boolean
  /** Callback to trigger bulk delete */
  onBulkDelete: () => void
  /** Callback to clear selection */
  onClearSelection: () => void
  /** Callback to open import dialog */
  onImport: () => void
  /** Set of selected policy IDs for export */
  selectedIds: Set<string>
  /** Callback to export selected policies */
  onExportSelected: () => void
  /** Callback to export all policies */
  onExportAll: () => void
  /** Whether an export is in progress */
  isExporting: boolean
}

/**
 * Toolbar for the policies list page
 * Includes search, status filter, and bulk actions
 */
export function PoliciesToolbar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  selectedCount,
  hasDeployedSelected,
  onBulkDelete,
  onClearSelection,
  onImport,
  selectedIds,
  onExportSelected,
  onExportAll,
  isExporting,
}: PoliciesToolbarProps) {
  const [localSearch, setLocalSearch] = useState(searchQuery)

  // Sync local search with prop when it changes externally
  useEffect(() => {
    setLocalSearch(searchQuery)
  }, [searchQuery])

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchQuery) {
        onSearchChange(localSearch)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [localSearch, searchQuery, onSearchChange])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(e.target.value)
    },
    []
  )

  const handleStatusChange = useCallback(
    (value: string) => {
      onStatusFilterChange(value as PolicyDraftStatus | '')
    },
    [onStatusFilterChange]
  )

  const currentStatusLabel =
    STATUS_OPTIONS.find((opt) => opt.value === statusFilter)?.label ||
    'All Statuses'

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Left side: Search and Filter */}
      <div className="flex flex-1 items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search policies..."
            value={localSearch}
            onChange={handleSearchChange}
            className="pl-8"
            aria-label="Search policies"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[140px]">
              {currentStatusLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {STATUS_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value || 'all'}
                onClick={() => handleStatusChange(option.value)}
                className={statusFilter === option.value ? 'bg-accent' : ''}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right side: Import/Export + Selection actions */}
      <div className="flex items-center gap-2">
        {selectedCount > 0 && (
          <>
            <Badge variant="secondary" className="px-3 py-1">
              {selectedCount} selected
            </Badge>
            <Button
              variant="destructive"
              size="sm"
              onClick={onBulkDelete}
              disabled={hasDeployedSelected}
              title={
                hasDeployedSelected
                  ? 'Cannot delete deployed policies'
                  : 'Delete selected policies'
              }
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        )}

        <Button variant="outline" size="sm" onClick={onImport} className="h-8">
          <Upload className="mr-2 h-3.5 w-3.5" />
          Import
        </Button>

        <PolicyExportButton
          selectedIds={selectedIds}
          onExportSelected={onExportSelected}
          onExportAll={onExportAll}
          isExporting={isExporting}
        />
      </div>
    </div>
  )
}
